/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : FreshnessIndicator
 *   Feature      : 226-universal-page-affordances
 *   Description  : Compact dual freshness label for the page header — "updated
 *                  Xs ago" (last client fetch, always) plus an optional
 *                  "· collected Xh ago" (DB collection time, best-effort). Makes
 *                  a fresh fetch of stale collected data legible instead of
 *                  misleadingly "current" (Spec 216 — the webapp shows
 *                  DB-collected data).
 *   Constitution : v1.23.0
 *   Author       : Peter Dobler
 *   Created      : 2026-07-07
 * ============================================================================
 */
import React, { useEffect, useState } from 'react';

export interface FreshnessIndicatorProps {
  /** When the client last completed a fetch. */
  lastFetchedAt?: Date | number | string | null;
  /** When the underlying data was collected (best-effort; omit if unknown). */
  collectedAt?: Date | number | string | null;
  className?: string;
}

function toMs(v: Date | number | string | null | undefined): number | null {
  // 0 is react-query's dataUpdatedAt before the first fetch: "never", not 1970.
  if (v == null || v === 0) return null;
  const t = v instanceof Date ? v.getTime() : new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
}

/** "5s ago" / "3m ago" / "2h ago" / "4d ago". */
export function relativeAgo(fromMs: number, nowMs: number = Date.now()): string {
  const s = Math.max(0, Math.floor((nowMs - fromMs) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export const FreshnessIndicator: React.FC<FreshnessIndicatorProps> = ({
  lastFetchedAt,
  collectedAt,
  className,
}) => {
  // Re-render on a timer so the relative "Xs ago" label actually ticks forward.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const fetched = toMs(lastFetchedAt);
  const collected = toMs(collectedAt);
  if (fetched == null && collected == null) return null;

  return (
    <span
      className={`text-xs text-[var(--color-text-secondary)] whitespace-nowrap ${className ?? ''}`}
    >
      {fetched != null && <>updated {relativeAgo(fetched)}</>}
      {collected != null && (
        <>
          {fetched != null && ' · '}
          collected {relativeAgo(collected)}
        </>
      )}
    </span>
  );
};

export default FreshnessIndicator;
